/**
 * Authoritative simulated day-night clock. Compresses a full day-night
 * cycle into `dayLengthMs` of real time (default ~2 real minutes, for fast
 * visible testing — a real deployment would likely use something like the
 * plan's suggested 10-20 real minutes per simulated day). `DayNightCycle`
 * (lighting/sky) is *driven* by this via `setTimeOfDay()` as of Phase 3,
 * rather than self-advancing as it did through Phase 1/2.
 *
 * Tracks simulated day boundaries separately from time-of-day, firing
 * `onNewDay` callbacks when a cycle completes, for `LongArcState` to persist.
 */
export class DayClock {
  private readonly dayLengthMs: number;
  private elapsedMs: number;
  private lastDayIndex: number;
  private readonly newDayCallbacks: Array<() => void> = [];

  constructor(dayLengthMs = 120_000, startTimeOfDay = 0.5) {
    this.dayLengthMs = dayLengthMs;
    this.elapsedMs = startTimeOfDay * dayLengthMs;
    this.lastDayIndex = Math.floor(this.elapsedMs / this.dayLengthMs);
  }

  update(deltaMs: number): void {
    this.elapsedMs += deltaMs;
    const dayIndex = Math.floor(this.elapsedMs / this.dayLengthMs);
    if (dayIndex > this.lastDayIndex) {
      this.lastDayIndex = dayIndex;
      for (const callback of this.newDayCallbacks) callback();
    }
  }

  getTimeOfDay(): number {
    return (this.elapsedMs % this.dayLengthMs) / this.dayLengthMs;
  }

  onNewDay(callback: () => void): void {
    this.newDayCallbacks.push(callback);
  }
}
